import { useEffect, useRef } from 'react';

import { useRouter, useToast } from '@kc-console/context';
import { getHttpStatus, STATUS_CODE, useTranslation } from '@kc-console/core';

import type { EnqueueErrorToastOptions } from '@kc-console/context';
import type { SnackbarMessage } from 'notistack';

export type DetailErrorMessage = SnackbarMessage | ((error: unknown) => SnackbarMessage);

export interface UseDetailErrorRedirectParams {
  // 상세 조회가 실패한 상태인지 여부입니다.
  isError: boolean;
  // 에러 처리 후 이동할 목록/상위 페이지 경로입니다.
  redirectTo: string;
  // 기본값은 true입니다. false이면 훅을 호출하더라도 토스트와 라우팅을 실행하지 않습니다.
  enabled?: boolean;
  // 토스트 메시지 생성과 상태 코드 판별(401 제외 등)에 사용됩니다.
  error?: unknown;
  // 고정 메시지 또는 에러 기반 동적 메시지입니다. 생략하면 기본 상세 로드 실패 문구를 사용합니다.
  message?: DetailErrorMessage;
  // 라우팅 전에 invalidate 같은 호출부 전용 후처리를 실행할 수 있습니다.
  onBeforeRedirect?: (error: unknown) => void | Promise<void>;
  // 기본값은 replace이며, false로 지정하면 push로 이동합니다.
  replace?: boolean;
  // 기본값은 모든 에러를 처리하는 함수입니다. 404만 이동하고 5xx는 현재 화면에 남기는 등 처리 대상을 좁힐 때 사용합니다.
  shouldHandle?: (error: unknown) => boolean;
  // scope, errorMessageMap 등 enqueueErrorToast 옵션을 추가로 전달합니다.
  toastOptions?: Omit<EnqueueErrorToastOptions, 'error'>;
}

/**
 * 페이지 상세 조회 실패 시 에러 토스트를 띄우고 목록/상위 페이지로 이동합니다.
 *
 * @description
 * - 화면 렌딩을 결정하는 "게이팅 API"의 실패만 이 훅으로 처리합니다. 상세 영역을 구성하는
 *   추가(보조) API의 실패는 각 섹션이 인라인으로 graceful 처리하며 이 훅을 거치지 않습니다.
 * - 401은 전역 Error401Modal(로그아웃 리다이렉트)이 소유하므로 이 훅은 401에서 아무 것도 하지 않습니다.
 * - 페이지는 상태와 무관하게 목록으로 이동하므로 토스트는 상태 코드 자동 매핑을 끄고(scope: null)
 *   "목록으로 이동" 안내 문구를 그대로 노출합니다. 필요 시 toastOptions로 재정의할 수 있습니다.
 * - 처리는 "isError 진입당 1회"입니다(handledRef). isError가 유지된 채 error 객체만 바뀌면 재처리하지 않으며,
 *   !isError로 떨어지면 가드가 초기화됩니다. 페이지 종착은 첫 에러에서 곧바로 이동하므로 실무상 충분합니다.
 * - 401 판별은 getHttpStatus(axios 에러에서만 status 추출)에 의존하므로 게이팅 쿼리는 axios 기반이어야 합니다.
 *
 * @default
 * - enabled: true
 * - redirectTo: 빈 문자열이면 토스트와 라우팅을 실행하지 않음
 * - message: common:common.status.detail_page_load_error
 * - replace: true
 * - shouldHandle: () => true
 *
 * @example
 * ```tsx
 * useDetailErrorRedirect({
 *   isError,
 *   error,
 *   redirectTo: getRouterPath(ROUTE_PATH.CLUSTER_LIST),
 * });
 * ```
 */
export default function useDetailErrorRedirect({
  isError,
  error,
  message,
  redirectTo,
  enabled = true,
  replace = true,
  shouldHandle = () => true,
  onBeforeRedirect,
  toastOptions,
}: UseDetailErrorRedirectParams) {
  const router = useRouter();
  const { t } = useTranslation();
  const detailErrorMessage = t('common:common.status.detail_page_load_error');
  const { enqueueErrorToast } = useToast();
  const handledRef = useRef(false);
  // 언마운트 시에만 true가 됩니다. 늦게 settle된 onBeforeRedirect promise가 언마운트 후 navigate하지
  // 않도록 가드하되, effect 재실행(deps 변경)과 진짜 언마운트를 구분하려고 effect-local 플래그가 아닌
  // 전용 ref를 씁니다. (effect-local 플래그는 router 등 deps가 리렌더마다 바뀌어 effect가 재실행되면
  // 진행 중인 이동을 취소해버리고, handledRef 가드로 재이동도 막혀 이동이 누락됩니다.)
  const unmountedRef = useRef(false);
  const hasRedirectTo = redirectTo.trim().length > 0;

  // message/shouldHandle/onBeforeRedirect/toastOptions는 호출부에서 매 렌더 새 참조로 전달될 수 있습니다
  // (특히 shouldHandle 기본값 () => true). 이를 effect deps에 두면 부모 리렌더마다 effect가 불필요하게
  // 재실행되므로 항상 최신값을 ref로만 읽어 effect를 isError 전이 중심으로 안정화합니다.
  // detailErrorMessage(기본 문구)도 같은 이유로 ref로 읽어 deps에서 제외합니다(t는 언어 변경 시에만 갱신).
  const latestRef = useRef({
    message,
    shouldHandle,
    onBeforeRedirect,
    toastOptions,
    detailErrorMessage,
  });
  latestRef.current = { message, shouldHandle, onBeforeRedirect, toastOptions, detailErrorMessage };

  // StrictMode 개발 모드에서는 effect setup/cleanup이 한 번 더 실행됩니다. cleanup에서 true로 바뀐 값을
  // 다음 setup에서 false로 복구해야 실제 mounted 상태의 redirect가 누락되지 않습니다.
  useEffect(() => {
    unmountedRef.current = false;

    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    // 에러가 해소되면 같은 컴포넌트 생명주기 안에서 다음 에러를 다시 처리할 수 있게 초기화합니다.
    if (!isError) {
      handledRef.current = false;
      return;
    }

    // 401은 전역 Error401Modal이 소유하므로 목록 리다이렉트/토스트를 실행하지 않습니다.
    if (getHttpStatus(error) === STATUS_CODE.UNAUTHORIZED) return;

    // 비활성 상태, 이동 경로 누락, 이미 처리한 에러, 호출부 필터에서 제외된 에러는 무시합니다.
    if (
      !enabled ||
      !hasRedirectTo ||
      handledRef.current ||
      !latestRef.current.shouldHandle(error)
    ) {
      return;
    }

    // rerender로 같은 에러 토스트와 라우팅이 반복되지 않도록 먼저 처리 완료로 표시합니다.
    handledRef.current = true;

    // 메시지가 함수이면 최신 error를 넘겨 호출부에서 상태 코드/도메인별 문구를 만들 수 있게 합니다.
    const resolveMessage = latestRef.current.message;
    const errorMessage =
      typeof resolveMessage === 'function'
        ? resolveMessage(error)
        : (resolveMessage ?? latestRef.current.detailErrorMessage);

    // 페이지는 어떤 상태든 목록으로 이동하므로 상태 코드 자동 매핑을 끄고(scope: null) 안내 문구를 그대로 노출합니다.
    enqueueErrorToast(errorMessage, {
      scope: null,
      ...latestRef.current.toastOptions,
      error,
    });

    // 상세 실패 후 히스토리에 잘못된 상세 URL이 남지 않도록 replace를 기본값으로 사용합니다.
    // (호출부가 히스토리를 남겨야 하는 경우에만 push를 사용합니다.)
    const redirect = () => {
      // 언마운트 후 늦게 도착한 promise는 이동을 건너뜁니다.
      if (unmountedRef.current) return;
      if (replace) {
        void router.replace(redirectTo);
        return;
      }
      void router.push(redirectTo);
    };

    // 리스트 invalidate처럼 라우팅 전에 필요한 도메인 후처리를 먼저 끝냅니다.
    // Promise를 반환하면 settle된 뒤 이동해 후처리와 네비게이션의 레이스를 방지합니다(reject돼도 best-effort로 이동).
    // 동기 throw도 try/catch로 감싸 best-effort로 이동시켜, 토스트만 뜨고 이동이 누락되는 비대칭을 막습니다.
    try {
      const beforeRedirect = latestRef.current.onBeforeRedirect?.(error);
      if (beforeRedirect instanceof Promise) {
        // resolve/reject 양쪽에서 이동시켜, reject 시에도 이동을 보장하고 unhandled rejection을 남기지 않습니다.
        // 언마운트 후 늦게 settle되면 redirect가 unmountedRef로 navigate를 건너뜁니다.
        void beforeRedirect.then(redirect, redirect);
        return;
      }
    } catch {
      // onBeforeRedirect의 동기 throw는 무시하고 아래에서 이동합니다.
    }

    redirect();
    // message/shouldHandle/onBeforeRedirect/toastOptions/detailErrorMessage는 latestRef로 최신값을 읽으므로
    // deps에서 제외해 콜백·문구 참조 변경에 따른 불필요한 effect 재실행(및 그로 인한 진행 중 이동 취소)을 방지합니다.
  }, [enabled, hasRedirectTo, isError, error, redirectTo, replace, enqueueErrorToast, router]);
}
